// CssEditorPopup.tsx

import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  PaperProps,
} from '@mui/material';
import CodeMirror from '@uiw/react-codemirror';
import { css } from '@codemirror/lang-css';
import { oneDark } from '@codemirror/theme-one-dark';
import { ViewUpdate } from '@codemirror/view';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import { Resizable } from 're-resizable';

interface CssEditorPopupProps {
  open: boolean;
  onClose: () => void;
  filename: string;
}

// Custom PaperComponent for draggable and resizable Dialog
const PaperComponent: React.FC<PaperProps> = (props) => {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Resizable
        defaultSize={{
          width: 600,
          height: 500,
        }}
        minWidth={400}
        minHeight={300}
        maxWidth={window.innerWidth - 100}
        maxHeight={window.innerHeight - 100}
        style={{ display: 'flex', flexDirection: 'column' }}
        enable={{
          top: true,
          right: true,
          bottom: true,
          left: true,
          topRight: true,
          bottomRight: true,
          bottomLeft: true,
          topLeft: true,
        }}
      >
        <Paper {...props} style={{ height: '100%', width: '100%', position: 'relative' }} />
      </Resizable>
    </Draggable>
  );
};

const CssEditorPopup: React.FC<CssEditorPopupProps> = ({ open, onClose, filename }) => {
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch CSS content when the popup opens or filename changes
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      setSuccess(null);
      fetch(
        `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=downloadCustomCss&filename=${encodeURIComponent(
          filename
        )}`
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch CSS content.');
          }
          return response.text();
        })
        .then((data) => {
          setContent(data);
          setOriginalContent(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [open, filename]);

  const reloadStyles = () => {
    const filenames = ['inlineStyles.css', 'paragraphStyles.css'];
    filenames.forEach((filename) => {
      const linkId = `custom-style-${filename}`;
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (link) {
        document.head.removeChild(link);
      }
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=downloadCustomCss&filename=${encodeURIComponent(
        filename
      )}&t=${new Date().getTime()}`;
      link.type = 'text/css';
      document.head.appendChild(link);
    });
  };

  const handleSave = (contentToSave: string = content) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Convert the CSS content to a Blob
    const blob = new Blob([contentToSave], { type: 'text/css' });
    const formData = new FormData();
    formData.append('file', blob, filename);
    formData.append('action', 'uploadCustomCss');

    fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}`, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to save CSS content.');
        }
        return response.json();
      })
      .then(() => {
        setSaving(false);
        setSuccess('CSS file saved successfully.');
        //setOriginalContent(contentToSave); // Update originalContent to the saved content
        reloadStyles();
      })
      .catch((err) => {
        setError(err.message);
        setSaving(false);
      });
  };

  const handleCancel = () => {
    setContent(originalContent); // Revert the editor content to originalContent
    handleSave(originalContent); // Save the reverted content to the server
    onClose(); // Close the popup dialog
    setError(null);
    setSuccess(null);
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploading(true);
      setError(null);
      setSuccess(null);
      const file = files[0];

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', 'uploadCustomCss');

      // Send the POST request to uploadCustomCss handler
      fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}`, {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('CSS file upload failed.');
          }
          return response.json();
        })
        .then(() => {
          setUploading(false);
          setSuccess(`CSS file "${file.name}" uploaded successfully.`);
          // Fetch the newly uploaded CSS content
          return fetch(
            `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=downloadCustomCss&filename=${encodeURIComponent(
              file.name
            )}`
          );
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch uploaded CSS content.');
          }
          return response.text();
        })
        .then((data) => {
          setContent(data);
          //setOriginalContent(data); // Update originalContent after upload
        })
        .catch((err) => {
          setError(err.message);
          setUploading(false);
        });
    }
  };

  const handleDownload = () => {
    // Initiate download via HandleCustomCssDownload
    const url = `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=downloadCustomCss&filename=${encodeURIComponent(
      filename
    )}`;
    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.blob();
        }
        throw new Error('Failed to download CSS file.');
      })
      .then((blob) => {
        if (blob.size === 0) {
          throw new Error('CSS file does not exist.');
        }
        // Create a link element to trigger download
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => {
        setError(err.message);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle
        style={{
          cursor: 'move',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        id="draggable-dialog-title"
      >
        Edit {filename}
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          edge="end"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {success && <Alert severity="success">{success}</Alert>}
            <CodeMirror
              value={content}
              height="100%"
              extensions={[css(), oneDark]}
              theme={oneDark}
              onChange={(value: string, viewUpdate: ViewUpdate) => setContent(value)}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: true,
                tabSize: 2,
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {/*
        <input
          accept=".css"
          style={{ display: 'none' }}
          id="upload-css-file"
          type="file"
          onChange={handleUpload}
        />
        <label htmlFor="upload-css-file">
          <Button component="span" startIcon={<UploadIcon />} disabled={uploading || saving}>
            Upload
          </Button>
        </label>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={loading || saving || uploading}
        >
          Download
        </Button>
        */}
        <Button
          onClick={()=>{handleSave(content)}}
          color="primary"
          variant="contained"
          disabled={saving || loading || uploading || !!error}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
        <Button onClick={handleCancel} disabled={saving || uploading}>
          Cancel
        </Button>
        <Button onClick={onClose} color="secondary" disabled={saving || uploading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CssEditorPopup;
